import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useAuth } from './AuthContext';

// Notification categories with colors
export const NOTIFICATION_CATEGORIES = {
  COMPLETION: { 
    name: 'Completions', 
    color: '#10B981', 
    bgColor: 'rgba(16, 185, 129, 0.1)',
    label: 'TASK COMPLETED'
  },
  UPLOAD: { 
    name: 'Uploads & Assets', 
    color: '#3B82F6', 
    bgColor: 'rgba(59, 130, 246, 0.1)',
    label: 'ASSET UPLOADED'
  },
  APPROVAL: { 
    name: 'Approvals & Reviews', 
    color: '#F59E0B', 
    bgColor: 'rgba(245, 158, 11, 0.1)',
    label: 'APPROVAL NEEDED'
  },
  COLLABORATION: { 
    name: 'Collaboration', 
    color: '#8B5CF6', 
    bgColor: 'rgba(139, 92, 246, 0.1)',
    label: 'COLLABORATION'
  },
  DEADLINE: { 
    name: 'Deadlines', 
    color: '#EF4444', 
    bgColor: 'rgba(239, 68, 68, 0.1)',
    label: 'DEADLINE'
  },
  PROGRESS: { 
    name: 'Progress Updates', 
    color: '#14B8A6', 
    bgColor: 'rgba(20, 184, 166, 0.1)',
    label: 'PROGRESS UPDATE'
  },
  SOULFOOD: { 
    name: 'Soul Food', 
    color: '#F59E0B', 
    bgColor: 'rgba(245, 158, 11, 0.1)',
    label: 'SOUL FOOD'
  }
};

// Demo notification sequence - 20 notifications
const DEMO_NOTIFICATION_SEQUENCE = [
  {
    category: 'COMPLETION',
    type: 'task_completed',
    headline: 'Task Completed — Marcus Rivera',
    detail: 'Just completed "Campaign brief draft" on Q2 Marketing Campaign. This was the last task blocking the Creative Review phase from advancing.',
    person: 'Marcus Rivera',
    project: 'Q2 Marketing Campaign',
    actions: [
      { label: 'Advance Phase', primary: true, navigate: '/projects' },
      { label: 'View Project', primary: false, navigate: '/projects' }
    ]
  },
  {
    category: 'UPLOAD',
    type: 'asset_uploaded',
    headline: 'Asset Uploaded — Lisa Chen',
    detail: 'Uploaded "brand_guidelines_v3.pdf" to Platform Evolution. Ready for review.',
    person: 'Lisa Chen',
    project: 'Platform Evolution',
    actions: [
      { label: 'View Asset', primary: true, navigate: '/projects' },
      { label: 'Request Review', primary: false, navigate: '/projects' }
    ]
  },
  {
    category: 'COMPLETION',
    type: 'phase_completed',
    headline: 'Phase Completed — Emma Thompson',
    detail: 'Marked "Design System Phase 1" complete on Platform Evolution. 3 phases remaining until project completion.',
    person: 'Emma Thompson',
    project: 'Platform Evolution',
    actions: [
      { label: 'Review Phase', primary: true, navigate: '/projects' },
      { label: 'Assign Next Phase', primary: false, navigate: '/projects' }
    ]
  },
  {
    category: 'APPROVAL',
    type: 'review_requested',
    headline: 'Review Requested — Jordan Lee',
    detail: 'Requesting feedback on "Soul Food Summer Series proposal" for Soul Food Nike Partnership. Submitted 2 minutes ago.',
    person: 'Jordan Lee',
    project: 'Soul Food Nike Partnership',
    actions: [
      { label: 'View & Comment', primary: true, navigate: '/projects' },
      { label: 'Assign Reviewer', primary: false, navigate: '/team' }
    ]
  },
  {
    category: 'PROGRESS',
    type: 'status_update',
    headline: 'Status Update Posted — James Chen',
    detail: 'Posted a progress update on Regional Expansion East.',
    person: 'James Chen',
    project: 'Regional Expansion East',
    actions: [
      { label: 'View Update', primary: true, navigate: '/projects' },
      { label: 'Reply', primary: false, navigate: '/threads' }
    ]
  },
  {
    category: 'COLLABORATION',
    type: 'team_member_onboarded',
    headline: 'New Team Member Onboarded',
    detail: 'David Kim completed their CognitiveOS onboarding. Operations thinker at L2. Ready for assignment.',
    person: 'David Kim',
    project: null,
    actions: [
      { label: 'View Profile', primary: true, navigate: '/team' },
      { label: 'Assign First Task', primary: false, navigate: '/tasks' }
    ]
  },
  {
    category: 'COMPLETION',
    type: 'all_tasks_cleared',
    headline: 'All Tasks Cleared — Sarah Chen',
    detail: 'Has completed her entire task queue and has no active assignments. Capacity available.',
    person: 'Sarah Chen',
    project: null,
    actions: [
      { label: 'Assign New Work', primary: true, navigate: '/tasks' },
      { label: 'View in Match Engine', primary: false, navigate: '/match' }
    ]
  },
  {
    category: 'UPLOAD',
    type: 'asset_uploaded',
    headline: 'Asset Uploaded — Marcus Rivera',
    detail: 'Uploaded "Q2_campaign_mockups_final.zip" to Q2 Marketing Campaign. Ready for review.',
    person: 'Marcus Rivera',
    project: 'Q2 Marketing Campaign',
    actions: [
      { label: 'View Asset', primary: true, navigate: '/projects' },
      { label: 'Request Review', primary: false, navigate: '/projects' }
    ]
  },
  {
    category: 'APPROVAL',
    type: 'signoff_required',
    headline: 'Sign-off Required — Platform Evolution',
    detail: 'Platform Evolution is ready to advance to the Launch Preparation phase but requires executive sign-off before proceeding. Emma Thompson is waiting.',
    person: 'Emma Thompson',
    project: 'Platform Evolution',
    actions: [
      { label: 'Review & Sign Off', primary: true, navigate: '/projects' },
      { label: 'Request More Info', primary: false, navigate: '/threads' }
    ]
  },
  {
    category: 'COLLABORATION',
    type: 'collab_started',
    headline: 'Collab Started — Jessica Park',
    detail: 'Launched "Nike Partnership Strategy" Collab with 4 members. First task has been assigned.',
    person: 'Jessica Park',
    project: 'Nike Partnership Strategy',
    actions: [
      { label: 'View Collab', primary: true, navigate: '/collab' },
      { label: 'View Members', primary: false, navigate: '/team' }
    ]
  },
  {
    category: 'PROGRESS',
    type: 'weekly_report',
    headline: 'Weekly Report Ready — Elena Rodriguez',
    detail: 'Submitted her weekly progress summary. 8 tasks completed, 3 in progress, 1 blocked.',
    person: 'Elena Rodriguez',
    project: null,
    actions: [
      { label: 'View Report', primary: true, navigate: '/insights' },
      { label: 'Reply', primary: false, navigate: '/threads' }
    ]
  },
  {
    category: 'PROGRESS',
    type: 'blocker_flagged',
    headline: 'Blocker Flagged — Jordan Lee',
    detail: 'Flagged a blocker on "Venue confirmation" in Soul Food Summer Series. This may affect the Event Prep phase timeline.',
    person: 'Jordan Lee',
    project: 'Soul Food Summer Series',
    actions: [
      { label: 'View Blocker', primary: true, navigate: '/tasks' },
      { label: 'Help Resolve', primary: false, navigate: '/threads' }
    ]
  },
  {
    category: 'COMPLETION',
    type: 'task_completed',
    headline: 'Task Completed — David Kim',
    detail: 'Completed "Infrastructure audit" on Platform Evolution.',
    person: 'David Kim',
    project: 'Platform Evolution',
    actions: [
      { label: 'View Project', primary: true, navigate: '/projects' },
      { label: 'View Profile', primary: false, navigate: '/team' }
    ]
  },
  {
    category: 'UPLOAD',
    type: 'final_deliverable',
    headline: 'Final Deliverable Submitted — Maya Johnson',
    detail: 'Submitted the final deliverable for Executive Briefing: "board_presentation_draft.pptx". Awaiting executive review and sign-off.',
    person: 'Maya Johnson',
    project: 'Executive Briefing',
    actions: [
      { label: 'Review & Approve', primary: true, navigate: '/projects' },
      { label: 'Request Changes', primary: false, navigate: '/threads' }
    ]
  },
  {
    category: 'DEADLINE',
    type: 'due_today',
    headline: 'Due Today — Regional Director Q2 review',
    detail: '"Regional Director Q2 review" assigned to Sarah Martinez is due today. Currently In Progress. 70% complete.',
    person: 'Sarah Martinez',
    project: null,
    actions: [
      { label: 'View Task', primary: true, navigate: '/tasks' },
      { label: 'Send Reminder', primary: false, navigate: '/threads' }
    ]
  },
  {
    category: 'COLLABORATION',
    type: 'comment_added',
    headline: 'Comment Added — Alexandra Chen',
    detail: 'Left a comment on "Strategic roadmap doc" in Platform Evolution that may require your input.',
    person: 'Alexandra Chen',
    project: 'Platform Evolution',
    actions: [
      { label: 'View Comment', primary: true, navigate: '/projects' },
      { label: 'Reply', primary: false, navigate: '/threads' }
    ]
  },
  {
    category: 'APPROVAL',
    type: 'revision_submitted',
    headline: 'Revision Submitted — Lisa Chen',
    detail: 'Submitted revised brand assets after incorporating feedback. Ready for final approval.',
    person: 'Lisa Chen',
    project: null,
    actions: [
      { label: 'Review Revision', primary: true, navigate: '/projects' },
      { label: 'Approve', primary: false, navigate: '/projects' }
    ]
  },
  {
    category: 'COMPLETION',
    type: 'phase_completed',
    headline: 'Phase Completed — Marcus Rivera',
    detail: 'Advanced Q2 Marketing Campaign to the "Launch Preparation" phase. 2 phases remaining.',
    person: 'Marcus Rivera',
    project: 'Q2 Marketing Campaign',
    actions: [
      { label: 'Review Phase', primary: true, navigate: '/projects' },
      { label: 'Assign Next Phase', primary: false, navigate: '/tasks' }
    ]
  },
  {
    category: 'SOULFOOD',
    type: 'partnership_updated',
    headline: 'Partnership Document Updated — Jordan Lee',
    detail: 'Updated the Nike partnership agreement in Soul Food Nike Partnership. Changes flagged for executive review.',
    person: 'Jordan Lee',
    project: 'Soul Food Nike Partnership',
    actions: [
      { label: 'View Document', primary: true, navigate: '/projects' },
      { label: 'Compare Versions', primary: false, navigate: '/projects' }
    ]
  },
  {
    category: 'COLLABORATION',
    type: 'collab_phase_advanced',
    headline: 'Collab Phase Advanced — Jessica Park',
    detail: 'Advanced Nike Partnership Strategy to "Contract Review" phase. All assigned members have been notified of their next tasks.',
    person: 'Jessica Park',
    project: 'Nike Partnership Strategy',
    actions: [
      { label: 'View Collab', primary: true, navigate: '/collab' },
      { label: 'View Tasks', primary: false, navigate: '/tasks' }
    ]
  }
];

const NotificationContext = createContext(null);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [toasts, setToasts] = useState([]);
  const [demoModeActive, setDemoModeActive] = useState(false);
  const demoIntervalRef = useRef(null);
  const sequenceIndexRef = useRef(0);
  const demoStartedRef = useRef(false);

  // Load notifications from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('cognitiveos_notifications');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setNotifications(parsed);
      } catch (e) {
        console.error('Failed to parse saved notifications:', e);
      }
    }
  }, []);

  // Save notifications to localStorage when they change
  useEffect(() => {
    localStorage.setItem('cognitiveos_notifications', JSON.stringify(notifications));
  }, [notifications]);

  // Auto-start demo mode when user is logged in
  // For this demo app, we treat all users as demo users
  useEffect(() => {
    if (user && !demoStartedRef.current) {
      demoStartedRef.current = true;
      setDemoModeActive(true);
      
      // Fire first notification after a short delay (3-8 seconds)
      const initialDelay = Math.floor(Math.random() * (8000 - 3000 + 1)) + 3000;
      
      demoIntervalRef.current = setTimeout(() => {
        fireNextDemoNotificationRef.current?.();
      }, initialDelay);
    }
    
    return () => {
      // Cleanup handled by unmount
    };
  }, [user]);

  const getUnreadCount = useCallback(() => {
    return notifications.filter(n => !n.read).length;
  }, [notifications]);

  const getUnreadCountByCategory = useCallback((category) => {
    if (category === 'ALL') {
      return notifications.filter(n => !n.read).length;
    }
    return notifications.filter(n => !n.read && n.category === category).length;
  }, [notifications]);

  const addNotification = useCallback((notification) => {
    const newNotification = {
      id: `notif_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date().toISOString(),
      read: false,
      dismissed: false,
      ...notification
    };
    
    setNotifications(prev => [newNotification, ...prev]);
    
    // Add toast
    setToasts(prev => {
      const newToasts = [...prev, { ...newNotification, toastId: `toast_${Date.now()}` }];
      // Max 3 toasts
      if (newToasts.length > 3) {
        return newToasts.slice(-3);
      }
      return newToasts;
    });

    return newNotification;
  }, []);

  const markAsRead = useCallback((notificationId) => {
    setNotifications(prev => 
      prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
    );
  }, []);

  const markAllAsRead = useCallback(() => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  }, []);

  const dismissNotification = useCallback((notificationId) => {
    setNotifications(prev => 
      prev.map(n => n.id === notificationId ? { ...n, dismissed: true } : n)
    );
  }, []);

  const dismissAllNotifications = useCallback(() => {
    setNotifications(prev => prev.map(n => ({ ...n, dismissed: true })));
  }, []);

  const removeToast = useCallback((toastId) => {
    setToasts(prev => prev.filter(t => t.toastId !== toastId));
  }, []);

  const clearAllNotifications = useCallback(() => {
    setNotifications([]);
    setToasts([]);
    sequenceIndexRef.current = 0;
  }, []);

  const fireNextDemoNotification = useCallback(() => {
    const notification = DEMO_NOTIFICATION_SEQUENCE[sequenceIndexRef.current];
    addNotification(notification);
    
    // Move to next notification, loop back to start
    sequenceIndexRef.current = (sequenceIndexRef.current + 1) % DEMO_NOTIFICATION_SEQUENCE.length;
    
    // Schedule next notification (7-45 seconds)
    const nextDelay = Math.floor(Math.random() * (45000 - 7000 + 1)) + 7000;
    demoIntervalRef.current = setTimeout(() => {
      fireNextDemoNotificationRef.current?.();
    }, nextDelay);
  }, [addNotification]);
  
  // Store the latest version of fireNextDemoNotification in a ref
  const fireNextDemoNotificationRef = useRef(fireNextDemoNotification);
  useEffect(() => {
    fireNextDemoNotificationRef.current = fireNextDemoNotification;
  }, [fireNextDemoNotification]);

  const startDemoMode = useCallback(() => {
    console.log('[NotificationContext] startDemoMode called, demoModeActive:', demoModeActive);
    if (demoModeActive) return;
    
    setDemoModeActive(true);
    console.log('[NotificationContext] Demo mode activated, scheduling first notification');
    
    // Fire first notification after a short delay
    const initialDelay = Math.floor(Math.random() * (10000 - 3000 + 1)) + 3000;
    console.log('[NotificationContext] First notification in', initialDelay, 'ms');
    demoIntervalRef.current = setTimeout(() => {
      console.log('[NotificationContext] Firing first notification via ref');
      fireNextDemoNotificationRef.current?.();
    }, initialDelay);
  }, [demoModeActive]);

  const stopDemoMode = useCallback(() => {
    setDemoModeActive(false);
    if (demoIntervalRef.current) {
      clearTimeout(demoIntervalRef.current);
      demoIntervalRef.current = null;
    }
  }, []);

  const value = {
    notifications: notifications.filter(n => !n.dismissed),
    allNotifications: notifications,
    toasts,
    demoModeActive,
    getUnreadCount,
    getUnreadCountByCategory,
    addNotification,
    markAsRead,
    markAllAsRead,
    dismissNotification,
    dismissAllNotifications,
    removeToast,
    clearAllNotifications,
    startDemoMode,
    stopDemoMode,
    NOTIFICATION_CATEGORIES
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationContext;
